import React, { useCallback, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';

import { token } from '@atlaskit/tokens';
import AlignTextLeftIcon from '@atlaskit/icon/core/align-text-left';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
import CrossCircle from '@atlaskit/icon/core/cross-circle';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { LinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { expVal } from '@confluence/feature-experiments';
import { useStarredData } from '@confluence/space-utils/entry-points/useSpacesData.ts';
import { SPACE_OVERVIEW, SPACE_DIRECTORY } from '@confluence/named-routes/entry-points/routes.ts';
import { SpaceIcon } from '@confluence/icons/entry-points/SpaceIcon.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { HoverPreloader, PreloadingPresets } from '@confluence/hover-preloader';

import { StarredSpacesEmptyState } from './StarredSpacesEmptyState.tsx';
import { StarredSpacesListLoadingSkeletons } from './LoadingSkeletons.tsx';

const fireStarredSpacesListItemClickedAnalytics = (
	createAnalyticsEvent: CreateUIAnalyticsEvent,
	navVersion: string,
	options: {
		componentType?: string;
		itemId?: string | null;
		itemType?: string;
		section?: string;
	} = {},
) => {
	const { componentType, itemId, itemType, section } = options;

	createAnalyticsEvent({
		type: 'sendUIEvent',
		data: {
			action: 'clicked',
			actionSubject: 'navigationItem',
			actionSubjectId: 'spacesNavigationMenuItem',
			source: 'globalNavigation',
			attributes: {
				navigationLayer: 'global',
				navigationContainer: 'sidebar',
				navVersion,
				componentType,
				itemId,
				itemType,
				section,
			},
		},
	}).fire();
};

const StarredSpacesListComponent = ({ flags }) => {
	const { isLicensed } = useSessionData();
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { starredSpaces, error, loading } = useStarredData({ isLicensed });
	const isStarredSpacesEmpty = !loading && starredSpaces.length === 0;
	const isMohitiEmptyStateEnabled =
		isStarredSpacesEmpty && expVal('cc_mohiti_nav_empty_states', 'isEnabled', false);

	const onViewAllClick = useCallback(() => {
		fireStarredSpacesListItemClickedAnalytics(createAnalyticsEvent, '4', {
			componentType: 'fixedItem',
			itemId: 'viewAllSpaces',
			section: 'starredSpaces',
		});
	}, [createAnalyticsEvent]);

	const onExploreSpacesClick = useCallback(() => {
		fireStarredSpacesListItemClickedAnalytics(createAnalyticsEvent, '4', {
			componentType: 'fixedItem',
			itemId: 'exploreSpaces',
			section: 'starredSpaces',
		});
	}, [createAnalyticsEvent]);

	const onStarredSpaceItemClick = useCallback(
		(spaceId: string | null) => {
			fireStarredSpacesListItemClickedAnalytics(createAnalyticsEvent, '4', {
				componentType: 'customItem',
				itemType: 'space',
				itemId: spaceId,
				section: 'starredSpaces',
			});
		},
		[createAnalyticsEvent],
	);

	useEffect(() => {
		if (!loading && error) {
			flags.showErrorFlag({
				icon: <CrossCircle label="" color={token('color.icon.danger')} />,
				title: <FormattedMessage {...i18n.errorFlagTitle} />,
				description: <FormattedMessage {...i18n.errorFlagDescription} />,
				isAutoDismiss: true,
			});
		}
	}, [loading, error, flags]);

	return (
		<MenuSection>
			<MenuSectionHeading>
				<FormattedMessage {...i18n.title} />
			</MenuSectionHeading>
			{loading && <StarredSpacesListLoadingSkeletons />}
			{isStarredSpacesEmpty &&
				(isMohitiEmptyStateEnabled ? (
					<StarredSpacesEmptyState onExploreSpacesClick={onExploreSpacesClick} />
				) : (
					<Box padding="space.100" xcss={emptySpacesTextStyles}>
						{intl.formatMessage(i18n.empty)}
					</Box>
				))}
			{!loading &&
				starredSpaces &&
				starredSpaces.map((space) => {
					const spaceAlias = space.alias || space.key;
					const url = SPACE_OVERVIEW.toUrl({ spaceKey: spaceAlias });
					return (
						<HoverPreloader
							key={space.id}
							name="starred-spaces"
							preloadUrl={url}
							preloadOptions={PreloadingPresets.onlyJSAndCriticalData}
						>
							<LinkMenuItem
								key={space.id}
								href={url}
								elemBefore={<SpaceIcon size="xsmall" src={space.icon?.path} />}
								onClick={() => onStarredSpaceItemClick(space.id)}
							>
								{space.name}
							</LinkMenuItem>
						</HoverPreloader>
					);
				})}
			{!loading && (!isMohitiEmptyStateEnabled || !isStarredSpacesEmpty) && (
				<LinkMenuItem
					href={SPACE_DIRECTORY.toUrl()}
					elemBefore={<AlignTextLeftIcon label="" color="currentColor" />}
					onClick={onViewAllClick}
				>
					<FormattedMessage {...i18n.viewAll} />
				</LinkMenuItem>
			)}
		</MenuSection>
	);
};

export const StarredSpacesList: React.ComponentType<
	Omit<
		{
			flags: any;
		},
		'flags'
	>
> = withFlags(StarredSpacesListComponent);

const emptySpacesTextStyles = xcss({
	color: 'color.text.subtlest',
	font: 'font.body.small',
});

const i18n = defineMessages({
	title: {
		id: 'side-navigation.starred-spaces-list.title',
		defaultMessage: 'Starred spaces',
		description:
			'The text is shown as the heading of the starred spaces list section in the side navigation panel, grouping the spaces the user has marked as starred for quick access.',
	},
	viewAll: {
		id: 'side-navigation.starred-spaces-list.view-all',
		defaultMessage: 'View all spaces',
		description:
			'The text is shown as a link label at the bottom of the starred spaces list in the side navigation panel. Clicking it navigates the user to the full spaces directory listing all available spaces.',
	},
	empty: {
		id: 'side-navigation.starred-spaces-list.empty-message',
		defaultMessage: 'Spaces you star will appear here',
		description: 'Displayed when there are no spaces to show in starred spaces list',
	},
	errorFlagTitle: {
		id: 'side-navigation.starred-spaces-list.error-flag-title',
		defaultMessage: 'We couldn’t load starred spaces',
		description: 'Title of error flag shown when starred spaces fail to load',
	},
	errorFlagDescription: {
		id: 'side-navigation.starred-spaces-list.error-flag-description',
		defaultMessage:
			'We are unable to show your spaces at this time. Refresh the page or try again.',
		description: 'Description of error flag shown when starred spaces fail to load',
	},
});
